import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/format";
import { GitFork, Split, ArrowRightLeft, Undo2, Cloud, EyeOff } from "lucide-react";

const KIND = {
    spinoff: { label: "Spin-off", icon: GitFork, cls: "bg-accent/15 text-accent border-accent/30" },
    split: { label: "Manual split", icon: Split, cls: "bg-primary/15 text-primary border-primary/30" },
    rename: { label: "Rename", icon: ArrowRightLeft, cls: "bg-gain/15 text-gain border-gain/30" },
    yahoo_split: { label: "Yahoo split", icon: Cloud, cls: "bg-muted text-muted-foreground border-border" },
};

function Item({ it, onUndo, onIgnore }) {
    const k = it.ignored ? { ...KIND.split, label: "Ignored Yahoo split" } : KIND[it.kind];
    const Icon = k.icon;
    return (
        <div className="flex items-start gap-3 py-3 border-t border-border/50 first:border-t-0" data-testid={`ca-item-${it.kind}-${it.id}`}>
            <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{it.title}</span>
                    <Badge variant="outline" className={`text-[10px] ${k.cls}`}>{k.label}</Badge>
                    <span className="text-xs text-muted-foreground num">{fmtDate(it.date)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{it.detail}</p>
            </div>
            {it.ignorable && (
                <Button size="sm" variant="ghost" className="h-8 text-xs gap-1 shrink-0" onClick={() => onIgnore(it)} data-testid={`ca-ignore-${it.id}`}>
                    <EyeOff className="w-3 h-3" />Ignore
                </Button>
            )}
            {it.undoable && (
                <Button size="sm" variant="ghost" className="h-8 text-xs gap-1 shrink-0" onClick={() => onUndo(it)} data-testid={`ca-undo-${it.kind}-${it.id}`}>
                    <Undo2 className="w-3 h-3" />Undo
                </Button>
            )}
        </div>
    );
}

export default function CorporateActions() {
    const qc = useQueryClient();
    const [filter, setFilter] = useState("mine");
    const [pending, setPending] = useState(null);
    const { data, isLoading } = useQuery({ queryKey: ["ca-log"], queryFn: async () => (await api.get("/corporate-actions/log")).data });
    const items = (data?.items || []).filter((i) => filter === "all" || i.kind !== "yahoo_split");

    const undo = async () => {
        const it = pending;
        setPending(null);
        try {
            const { data: r } = await api.post("/corporate-actions/undo", { kind: it.kind, id: it.id });
            toast.success(r.message);
            qc.invalidateQueries();
        } catch (e) { toast.error(apiErr(e)); }
    };

    const ignore = async (it) => {
        const [ticker, date] = it.id.split(":");
        try {
            const { data: r } = await api.post("/corporate-actions/splits/ignore", { ticker, date });
            toast.success(`${r.message} — Undo is in the list under 'Recorded by me'`);
            qc.invalidateQueries();
        } catch (e) { toast.error(apiErr(e)); }
    };

    return (
        <div className="space-y-5" data-testid="corporate-actions-page">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-sm text-muted-foreground">Every spin-off, manual split and ticker rename you've recorded, newest first. Renames are logged from today on.</p>
                </div>
                <div className="flex gap-1 rounded-lg border border-border p-0.5 text-xs">
                    {[["mine", "Recorded by me"], ["all", "Include Yahoo splits"]].map(([v, l]) => (
                        <button key={v} onClick={() => setFilter(v)} data-testid={`ca-filter-${v}`}
                            className={`px-2.5 py-1 rounded-md transition-colors ${filter === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{l}</button>
                    ))}
                </div>
            </div>
            <Card className="px-4 py-1" data-testid="ca-list">
                {isLoading ? <Skeleton className="h-24 my-3" /> : items.length
                    ? items.map((it) => <Item key={`${it.kind}-${it.id}`} it={it} onUndo={setPending} onIgnore={ignore} />)
                    : <p className="text-sm text-muted-foreground py-6 text-center" data-testid="ca-empty">Nothing recorded yet. Spin-offs and renames are recorded from Open Issues.{filter === "mine" && (data?.items || []).length > 0 && " Choose 'Include Yahoo splits' to see the splits applied automatically."}</p>}
            </Card>
            <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
                <AlertDialogContent data-testid="ca-undo-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Undo “{pending?.title}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {pending?.kind === "spinoff" && "The new shares are removed and the cost basis goes back to the parent."}
                            {pending?.kind === "split" && (pending?.ignored ? "Yahoo's split for this date is applied to your share counts again." : "Your manual split is removed; Yahoo's data for that date applies again.")}
                            {pending?.kind === "rename" && "The moved transactions go back to the old ticker, with the original quantities and prices."}
                            {" "}It's blocked if a later sale depends on it.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="ca-undo-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={undo} data-testid="ca-undo-confirm">Undo</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
